import { useState } from "react";

type PersistedState = {
  timestamp: number;
  value: unknown;
};

const OUTDATED_KEYS_CLEANUP_INTERVAL = 1000 * 60 * 60 * 24; // 24 hours
const KEY_PREFIX = "heimdall:";

const get = (key: string): PersistedState | null => {
  const storageKey = `${KEY_PREFIX}${key}`;
  const content = localStorage.getItem(storageKey);

  if (!content) {
    return null;
  }

  try {
    return JSON.parse(content);
  } catch (error) {
    return null;
  }
};

const set = (key: string, value: unknown) => {
  const storageKey = `${KEY_PREFIX}${key}`;

  const object: PersistedState = {
    timestamp: Date.now(),
    value: value,
  };

  const content = JSON.stringify(object);

  localStorage.setItem(storageKey, content);
};

export const removePersistedData = (key: string) => {
  const storageKey = `${KEY_PREFIX}${key}`;
  localStorage.removeItem(storageKey);
};

export const clearOutdatedPersistedData = () => {
  const now = Date.now();

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);

    // should never happen but for type safety
    if (!key) {
      continue;
    }

    // only clean up keys that belong to this app
    if (!key.startsWith(KEY_PREFIX)) {
      continue;
    }

    const keyWithoutPrefix = key.slice(KEY_PREFIX.length);

    const persistedState = get(keyWithoutPrefix);

    // should never happen but for type safety
    if (persistedState == null) {
      continue;
    }

    if (typeof persistedState.timestamp !== "number") {
      localStorage.removeItem(key);
      continue;
    }

    if (now - persistedState.timestamp > OUTDATED_KEYS_CLEANUP_INTERVAL) {
      console.info(`Removing outdated persisted data for key: ${key}`);
      localStorage.removeItem(key);
    }
  }
};

export const usePersistedState = <T>(
  key: string,
  initialValue: () => T,
): [T, React.Dispatch<(prevState: T) => T>, () => void] => {
  const [state, setState] = useState<T>(() => {
    const persistedState = get(key);
    return persistedState != null ? (persistedState.value as T) : initialValue();
  });

  const setState_ = (update: (prevState: T) => T) => {
    const newState = update(state);
    set(key, newState);
    setState(newState);
  };

  const clear = () => removePersistedData(key);

  return [state, setState_, clear];
};
